if (!customElements.get("page-predictive-search")) {
  class PagePredictiveSearch extends HTMLElement {
    constructor() {
      super();

      this.cache = new Map();
      this.abortController = null;
    }

    connectedCallback() {
      this.input = this.querySelector("[data-page-search-input]");
      this.results = this.querySelector("[data-page-predictive-results]");
      this.clearButton = this.querySelector("[data-page-search-clear]");
      this.status = this.querySelector("[data-page-search-status]");
      this.form = this.querySelector("form");

      if (!this.input || !this.results || !this.form) return;

      this.inputHandler = this.debounce(
        this.handleInput.bind(this),
        300
      );

      this.input.addEventListener("input", this.inputHandler);

      this.input.addEventListener("focus", () => {
        if (
          this.input.value.trim().length &&
          this.results.innerHTML.trim().length
        ) {
          this.open();
        }
      });

      if (this.clearButton) {
        this.clearButton.addEventListener("click", () => {
          this.clearSearch();
        });
      }

      this.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
          this.close();
          this.input.focus();
        }
      });

      document.addEventListener("click", (event) => {
        if (!this.contains(event.target)) {
          this.close();
        }
      });
    }

    async handleInput() {
      const searchTerm = this.input.value.trim();

      this.toggleClearButton(searchTerm.length > 0);

      if (!searchTerm.length) {
        this.abortController?.abort();
        this.results.innerHTML = "";
        this.close();
        return;
      }

      await this.getSearchResults(searchTerm);
    }

    async getSearchResults(searchTerm) {
      const cacheKey = searchTerm.toLowerCase();

      if (this.cache.has(cacheKey)) {
        this.renderResults(
          this.cache.get(cacheKey),
          searchTerm
        );
        return;
      }

      this.abortController?.abort();

      const controller = new AbortController();
      this.abortController = controller;

      this.setLoading(true);

      try {
        const endpoint = new URL(
          this.dataset.predictiveUrl,
          window.location.origin
        );

        endpoint.searchParams.set("q", searchTerm);
        endpoint.searchParams.set(
          "resources[type]",
          "query,product,collection"
        );
        endpoint.searchParams.set("resources[limit]", "4");
        endpoint.searchParams.set(
          "resources[limit_scope]",
          "each"
        );
        endpoint.searchParams.set(
          "resources[options][unavailable_products]",
          "last"
        );
        endpoint.searchParams.set(
          "resources[options][fields]",
          "title,product_type,vendor,variants.sku,variants.title,tag"
        );
        endpoint.searchParams.set(
          "section_id",
          "predictive-search"
        );

        const response = await fetch(endpoint.toString(), {
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(
            `Predictive search request failed: ${response.status}`
          );
        }

        const responseText = await response.text();

        const responseDocument = new DOMParser().parseFromString(
          responseText,
          "text/html"
        );

        const section = responseDocument.querySelector(
          "#shopify-section-predictive-search"
        );

        if (!section) {
          throw new Error(
            "The predictive-search section was not found."
          );
        }

        const markup = section.innerHTML;

        this.cache.set(cacheKey, markup);

        if (this.input.value.trim() === searchTerm) {
          this.renderResults(markup, searchTerm);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error(error);
          this.renderError();
        }
      } finally {
        if (this.abortController === controller) {
          this.setLoading(false);
        }
      }
    }

    renderResults(markup, searchTerm) {
      this.results.innerHTML = markup;

      const resultItems =
        this.results.querySelectorAll(".m-search-item");

      if (!resultItems.length) {
        const emptyMessage = document.createElement("div");

        emptyMessage.className =
          "page-predictive-search__empty";

        emptyMessage.textContent =
          this.dataset.emptyText ||
          "No matching suggestions found.";

        this.results.innerHTML = "";
        this.results.appendChild(emptyMessage);
      }

      this.addViewAllLink(searchTerm);

      if (this.status) {
        this.status.textContent = resultItems.length
          ? `${resultItems.length} suggestions available.`
          : "No suggestions found.";
      }

      this.open();
    }

    addViewAllLink(searchTerm) {
      const searchURL = new URL(
        this.form.action,
        window.location.origin
      );

      searchURL.searchParams.set("q", searchTerm);
      searchURL.searchParams.set("type", "product");
      searchURL.searchParams.set(
        "options[prefix]",
        "last"
      );

      const link = document.createElement("a");

      link.className =
        "page-predictive-search__view-all";

      link.href = `${searchURL.pathname}${searchURL.search}`;

      link.textContent = `${
        this.dataset.viewAllText || "View all results for"
      } “${searchTerm}”`;

      this.results.appendChild(link);
    }

    renderError() {
      this.results.innerHTML = "";

      const errorMessage = document.createElement("div");

      errorMessage.className =
        "page-predictive-search__empty";

      errorMessage.textContent =
        "Search suggestions could not be loaded. Press Enter to search.";

      this.results.appendChild(errorMessage);

      this.open();
    }

    open() {
      this.results.hidden = false;
      this.input.setAttribute("aria-expanded", "true");
      this.setAttribute("open", "");
    }

    close() {
      this.results.hidden = true;
      this.input.setAttribute("aria-expanded", "false");
      this.removeAttribute("open");
    }

    clearSearch() {
      this.abortController?.abort();

      this.input.value = "";
      this.results.innerHTML = "";

      this.toggleClearButton(false);
      this.close();
      this.input.focus();

      if (this.status) {
        this.status.textContent = "";
      }
    }

    toggleClearButton(show) {
      if (!this.clearButton) return;

      this.clearButton.hidden = !show;
    }

    setLoading(loading) {
      this.toggleAttribute("loading", loading);
      this.input.setAttribute(
        "aria-busy",
        loading ? "true" : "false"
      );
    }

    debounce(callback, delay) {
      let timeout;

      return (...args) => {
        clearTimeout(timeout);

        timeout = setTimeout(() => {
          callback.apply(this, args);
        }, delay);
      };
    }
  }

  customElements.define(
    "page-predictive-search",
    PagePredictiveSearch
  );
}